import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import TriggerRulesList, { type WorkflowRecord } from "./TriggerRulesList";
import TriggerRulesEditor from "./TriggerRulesEditor";
import { TriggerTypeSelectionPanel } from "./components/TriggerTypeSelectionPanel";
import { useWorkflow } from "./hooks/useWorkflow";
import { useWorkflows } from "./hooks/useWorkflows";
import HButton from "../../components/custom/input/button/Button";
import { Trigger, type TriggerType } from "./nodes/TriggerNode";
import "./triggerRules.css";

const TriggerRules: React.FC = () => {
    const { workflowId: idFromUrl } = useParams<{ workflowId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowRecord | null | "new">(null);
    const [triggerTypeForNew, setTriggerTypeForNew] = useState<TriggerType | null>(null);

    const workflows = useWorkflows();

    const activeWorkflowId = selectedWorkflow !== null && selectedWorkflow !== "new"
        ? selectedWorkflow.workflowId
        : null;
    const { workflow: serverWorkflow, isLoading: workflowLoading } = useWorkflow(activeWorkflowId);

    useEffect(() => {
        if (!idFromUrl) {
            setSelectedWorkflow(null);
            setTriggerTypeForNew(null);
            return;
        }
        if (idFromUrl === "new") {
            setSelectedWorkflow("new");
            return;
        }
        setSelectedWorkflow((prev) => {
            if (typeof prev === "object" && prev !== null && prev.workflowId === idFromUrl) {
                return prev;
            }
            return {
                workflowId: idFromUrl,
                workflowName: "",
                status: "",
                triggerType: Trigger.Message.Inbound,
                direction: "Incoming",
                runs: 0,
            };
        });
    }, [idFromUrl]);

    const handleWorkflowClick = (workflow: WorkflowRecord) => {
        setSelectedWorkflow(workflow);
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules/${workflow.workflowId}${location.search}`);
    };

    const handleNewWorkflow = () => {
        setSelectedWorkflow("new");
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules/new${location.search}`);
    };

    const handleBack = () => {
        setSelectedWorkflow(null);
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules${location.search}`);
    };

    const getTriggerType = (): TriggerType => {
        if (selectedWorkflow !== null && selectedWorkflow !== "new") {
            return selectedWorkflow.direction === "Outgoing" ? Trigger.Message.Outbound : Trigger.Message.Inbound;
        }
        return triggerTypeForNew ?? Trigger.Message.Inbound;
    };

    const handleWorkflowSaved = (newWorkflowId: string): void => {
        const triggerType = getTriggerType();
        setSelectedWorkflow({
            workflowId: newWorkflowId,
            workflowName: "",
            status: "",
            triggerType,
            direction: (triggerType === Trigger.Message.Outbound ? "Outgoing" : "Incoming") as "Incoming" | "Outgoing",
            runs: 0,
        });
        navigate(`/messaging/automation/trigger-rules/${newWorkflowId}${location.search}`);
    };

    const handleTriggerTypeConfirm = (triggerType: TriggerType): void => {
        setTriggerTypeForNew(triggerType);
    };

    const handleTriggerTypeCancel = (): void => {
        setSelectedWorkflow(null);
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules${location.search}`);
    };

    if (selectedWorkflow === null) {
        return (
            <TriggerRulesList
                workflows={workflows}
                onWorkflowClick={handleWorkflowClick}
                onNewWorkflow={handleNewWorkflow}
            />
        );
    }

    if (selectedWorkflow === "new" && triggerTypeForNew === null) {
        return (
            <Flex className="trigger-rules">
                <div className="d-flex flex-column flex-fill hs-min-h-0">
                    <Flex className="py-3 px-3 hs-border-bottom-light" justify="space-between" align="center">
                        <Flex align="center" gap={12}>
                            <Input
                                value="New Workflow"
                                disabled
                                suffix={<EditOutlined aria-hidden />}
                                aria-label="Workflow name"
                            />
                        </Flex>
                        <Flex gap={12} wrap="wrap">
                            <HButton
                                size="s"
                                type="text"
                                buttonClassName="hs-color-BlackRussian"
                                onClick={handleTriggerTypeCancel}
                            >
                                Cancel
                            </HButton>
                            <HButton size="s" type="primary" disabled>
                                Save
                            </HButton>
                        </Flex>
                    </Flex>
                    <div className="d-flex flex-fill hs-min-h-0">
                        <div className="position-relative hs-bg-light-grey-1 d-flex justify-content-center align-items-center flex-fill">
                            <div className="automationCardsSprite trigger-rules-trigger-center-illustration" aria-hidden></div>
                        </div>
                    </div>
                </div>
                <div className="hs-w-400-px hs-border-left-light">
                    <TriggerTypeSelectionPanel
                        onConfirm={handleTriggerTypeConfirm}
                        onCancel={handleTriggerTypeCancel}
                    />
                </div>
            </Flex>
        );
    }


    if (activeWorkflowId && workflowLoading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ position: "fixed", inset: 0, zIndex: 10 }}
            >
                <div className="actionIconsSprite dropDownLoading" />
            </div>
        );
    }

    return (
        <TriggerRulesEditor
            workflow={selectedWorkflow === "new" ? null : selectedWorkflow}
            serverWorkflow={serverWorkflow}
            onBack={handleBack}
            onWorkflowSaved={handleWorkflowSaved}
            triggerType={getTriggerType()}
        />
    );
};

export default TriggerRules;
